import React, { useState, useContext, useEffect } from 'react';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterIcon,
  TwitterShareButton,
} from 'react-share';
import styled from 'styled-components';
import Button from '@components/buttons';
import { Box } from '@components/layout/Grid';
import { H4 } from '@components/typography/Headings';
import { Trans } from '@lingui/react';
import Banner from '@organisms/Banner';
import LanguageContext from '@lib/contexts/languageContext';
import { Body, Header, Modal } from './base';
import PlaylistModal from './PlaylistModal';
import { UserContext } from '@lib/contexts/UserProvider';
import AnalyticsManager from '@lib/analytics/manager';
import { CopyInputGroup } from '@organisms/CopyInputGroup';
const IconsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;

  .react-share-icon:hover {
    cursor: pointer;
  }
`;

const size = 30;

const IconStyle = {
  marginRight: '10px',
  borderRadius: '3px',
  overflow: 'hidden',
};

const renderPrivacyBanner = playlist => {
  if (playlist.visibility === 'public') return null;

  return (
    <Box mb={25}>
      <Banner
        style={{
          marginBottom: '30px',
          backgroundColor: '#FFE9CB',
        }}
        iconColor={'#242424'}
      >
        <PlaylistModal key="playlist" playlist={playlist}>
          {openModal =>
            playlist.visibility === 'hidden' ? (
              <Trans
                id={'share_modal.hidden'}
                message={
                  'You can share this playlist but it can only be accessed via the link/URL. <0>Edit playlist settings.</0>'
                }
                components={[
                  <Button
                    key="privacy"
                    appearance="link"
                    onClick={openModal}
                  />,
                ]}
              />
            ) : (
              <Trans
                id={'share_modal.privacy'}
                message={
                  'You can still share this playlist but no one will be able to see it. <0>Edit playlist settings.</0>'
                }
                components={[
                  <Button
                    key="privacy"
                    appearance="link"
                    onClick={openModal}
                  />,
                ]}
              />
            )
          }
        </PlaylistModal>
      </Banner>
    </Box>
  );
};

const ShareModal = ({ subject = 'Yogobe Video', ...props }) => {
  const [open, setOpen] = useState(false);
  const lang = useContext(LanguageContext);
  const [currentUser, { loading }] = useContext(UserContext);

  useEffect(() => {
    if (currentUser?.id && open && props.resource === 'videos') {
      AnalyticsManager().sendClickedShareVideoEvent({
        videoId: props.resourceIdentifier,
      });
    }
  }, [open]);

  const formattedUrl = ({ id, resource = 'videos' }) => {
    const domain = process.env.DOMAIN;
    if (resource === 'program') return id;
    return `${domain}/${lang}/${resource}/${id}`;
  };

  const iconParams = {
    className: 'react-share-icon',
    style: IconStyle,
    url:
      props.shareURL ??
      formattedUrl({
        id: props.resourceIdentifier,
      }),
  };

  const handleCloseModal = () => {
    setOpen(false);
    props.onModalClose?.();
  };
  const handleOpenModal = () => setOpen(true);

  if (loading) {
    return null;
  }

  return (
    <React.Fragment>
      <Modal
        open={open}
        handleClose={handleCloseModal}
        customWidth="500px"
        data-testid="ShareModal"
      >
        <Header handleClose={handleCloseModal}>
          <Box as={H4} mt="0px" mb="0px">
            <Trans id="share_modal.title" message={'Share'} />
          </Box>
        </Header>
        <Body pb={30}>
          {currentUser?.id && currentUser?.id === props.playlist?.ownerId
            ? renderPrivacyBanner(props.playlist)
            : null}
          <Box mb={25}>
            <IconsContainer>
              <FacebookShareButton {...iconParams}>
                <FacebookIcon size={size} />
              </FacebookShareButton>
              <TwitterShareButton {...iconParams}>
                <TwitterIcon size={size} />
              </TwitterShareButton>
              <EmailShareButton
                {...iconParams}
                subject={subject}
                body={
                  props.shareURL ??
                  formattedUrl({
                    id: props.resourceIdentifier,
                  })
                }
              >
                <EmailIcon size={size} />
              </EmailShareButton>
            </IconsContainer>
          </Box>
          <Box>
            <CopyInputGroup
              title={
                <Trans
                  id="share_modal.copy_placeholder"
                  message={'Copy this url to share:'}
                />
              }
              value={
                props.shareURL ??
                formattedUrl({
                  id: props.resourceIdentifier,
                  resource: props.resource,
                })
              }
            />
          </Box>
        </Body>
      </Modal>
      {props.children(handleOpenModal)}
    </React.Fragment>
  );
};

export default ShareModal;
